import {
  useExecuteCancelInvalidTpSlOrders,
} from 'client/hooks/execute/cancelOrder/useExecuteCancelInvalidTpSlOrders';
import { useIsSingleSignatureSession } from 'client/modules/singleSignatureSessions/hooks/useIsSingleSignatureSession';
import { useOrphanedTpSlStatus } from 'client/modules/trading/hooks/useOrphanedTpSlStatus';
import { useSuppressedOrphanedTpSlDigests } from 'client/modules/trading/hooks/useSuppressedOrphanedTpSlDigests';
import { delay } from 'client/utils/delay';
import { join } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NUM_RETRIES = 5;
// After all retries are exhausted, wait this long before trying the same
// order set again. Prevents infinite retry loops that cause 429 rate limits.
const COOLDOWN_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Cancels TP/SL orders when a position changes (e.g. closed, side change, order fill).
 *
 * When the linked signer is stale (backend has a signer but the local session
 * key is gone), cancellation will always fail. In that case we:
 *   1. Warn once via console — the user needs to re-approve 1CT.
 *   2. After retries are exhausted, persist the failing digests to localStorage
 *      so they are suppressed from future retry loops.
 *   3. Clear suppressed digests when cancellation succeeds (signer refreshed).
 */
export function TpSlPositionChangeListener() {
  const isSingleSignatureSession = useIsSingleSignatureSession({
    requireActive: true,
  });

  const { orphanedOrders, signerIsStale } = useOrphanedTpSlStatus();
  const { suppressDigests, clearSuppressedDigests } =
    useSuppressedOrphanedTpSlDigests();

  // Since this hook can run while a cancel request is in flight, we use the following
  // state to ensure that only one request is in flight at a given time. We want a bit
  // of a delay between consecutive requests, so we cannot rely on mutation state here.
  const [isCancelling, setIsCancelling] = useState(false);

  // Track signals that have exhausted all retries with a cooldown timestamp.
  // Prevents infinite retry loops when cancellation permanently fails
  // (e.g., expired linked signer, trigger service auth issues).
  const failedSignalsRef = useRef<Map<string, number>>(new Map());
  const lastSignalRef = useRef<string | undefined>();
  // Store the orders for the last signal so we can suppress digests on failure
  const lastOrdersRef = useRef<string[]>([]);

  const { mutate, failureCount, status } =
    useExecuteCancelInvalidTpSlOrders({
      // By default, react-query uses exponential backoff
      retry: NUM_RETRIES,
      onSettled() {
        // Inject a delay to avoid potential race conditions with order refetching
        delay(1000).then(() => {
          setIsCancelling(false);
        });
      },
    });

  // Record failed signals after retry exhaustion
  const onMutationComplete = useCallback(() => {
    const signal = lastSignalRef.current;
    if (!signal) return;

    if (status === 'error') {
      failedSignalsRef.current.set(signal, Date.now());
      // Persist the failing digests to localStorage so they are skipped on
      // future runs. This prevents an infinite retry loop when the linked
      // signer is stale and cannot be refreshed without user action.
      if (lastOrdersRef.current.length > 0) {
        suppressDigests(lastOrdersRef.current);
      }
    } else if (status === 'success') {
      failedSignalsRef.current.delete(signal);
      // Cancellation succeeded — clear any previously suppressed digests so
      // they don't block future auto-cancellations.
      clearSuppressedDigests();
    }
  }, [clearSuppressedDigests, status, suppressDigests]);

  useEffect(() => {
    if (status === 'error' || status === 'success') {
      onMutationComplete();
    }
  }, [status, onMutationComplete]);

  const isRetrying = !!failureCount && failureCount < NUM_RETRIES;
  const shouldRunEffectSignal = useMemo(() => {
    /**
     * Conditions for running this effect
     * - Positions & trigger orders have loaded (orphanedOrders will be empty until then)
     * - 1CT is enabled
     * - Not currently cancelling
     * - Not currently retrying a failed cancel request
     */
    if (
      !isSingleSignatureSession ||
      isCancelling ||
      isRetrying
    ) {
      return;
    }

    // orphanedOrders already has suppressed digests filtered out
    if (!orphanedOrders.length) {
      return;
    }

    // When the local linked signer is stale, cancellation will always be
    // rejected by the trigger service. Warn once and bail out — the user
    // needs to re-approve 1CT before we can auto-cancel.
    if (signerIsStale) {
      console.warn(
        '[TpSlPositionChangeListener] Skipping auto-cancel: local linked signer is stale. ' +
          'Re-approve 1-Click Trading to re-enable auto-cancellation of orphaned TP/SL orders.',
        { orphanedOrderCount: orphanedOrders.length },
      );
      return;
    }

    const signalId = getSignalId(orphanedOrders.map((o) => o.digest));

    // Skip signals that are in cooldown after exhausting all retries.
    const failedAt = failedSignalsRef.current.get(signalId);
    if (failedAt && Date.now() - failedAt < COOLDOWN_MS) {
      return;
    }

    return signalId;
  }, [
    isCancelling,
    isRetrying,
    isSingleSignatureSession,
    orphanedOrders,
    signerIsStale,
  ]);

  useEffect(() => {
    if (!shouldRunEffectSignal) {
      return;
    }

    lastSignalRef.current = shouldRunEffectSignal;
    lastOrdersRef.current = orphanedOrders.map((o) => o.digest);
    setIsCancelling(true);
    mutate({});
  }, [mutate, orphanedOrders, shouldRunEffectSignal]);

  return null;
}

/**
 * Creates a deterministic signal ID based on the digests of the orders to cancel.
 */
function getSignalId(digests: string[]) {
  return join([...digests].sort());
}
